/**
 * RBACDashboardPage — Consolidated RBAC management page.
 *
 * Wraps the existing RoleManagementDashboard (9-tab dashboard from workflow-forms)
 * with portal-level auth context (tenantId, navigation).
 *
 * Routes:
 *   /admin/rbac — Full RBAC dashboard (roles, permissions, hierarchy, audit, JIT)
 *
 * Accessible from Admin/Architect > Governance > RBAC Dashboard.
 */

import { Container, Title, Group, Badge, Text, ThemeIcon } from '@mantine/core';
import { IconShieldLock } from '@tabler/icons-react';

import { RoleManagementDashboard } from '@ferp/workflow-forms';
import { useAuth, useViewLayer } from '@/auth/AuthContext';

export function RBACDashboardPage() {
  const { tenant } = useAuth();
  const viewLayer = useViewLayer();

  // Portal knows the real auth role — admin/architect can manage roles
  // even when useRBAC falls back to 'viewer' (backend RBAC query unavailable)
  const allowAccess = viewLayer === 'admin' || viewLayer === 'architect';

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="indigo">
            <IconShieldLock size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>RBAC & Roles</Title>
            <Text size="sm" c="dimmed">
              Role management, permissions, hierarchy, and audit trails
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      <RoleManagementDashboard
        tenantId={tenant?.id}
        autoRefresh
        allowAccess={allowAccess}
      />
    </Container>
  );
}
